import { useCallback, useContext, useEffect } from "react";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { message } from "antd";
import {
  ErrorMessages,
  ProjectPermissionEnum,
  SuccessMessages
} from "constants/index";
import {
  // useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import { QUERY_CUSTOM_REPORT_DETAILS_BY_ID } from "../gql/query";
import { MUTATION_UPDATE_PROJECT_REPORT } from "../gql/mutation";

function useManageConfigs(customReportId: string) {
  const dispatch: any = useCustomReportsDispatch();

  const { gqlClientForProject, tokenContents } = useContext(ProjectContext);

  const canConfigureCustomReports = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanConfigureCustomReports,
    tokenContents?.role!
  );

  const { data: customReportDetails } = useCIQQuery(
    QUERY_CUSTOM_REPORT_DETAILS_BY_ID,
    {
      variables: { id: customReportId },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    }
  );

  const [updateCustomReport] = useCIQMutation(MUTATION_UPDATE_PROJECT_REPORT, {
    client: gqlClientForProject
  });

  useEffect(() => {
    if (!customReportDetails?.project_reports_by_pk?.query_configs) return;

    const queryConfigs =
      customReportDetails.project_reports_by_pk.query_configs;

    const groupByFieldKey =
      queryConfigs.group_by_config?.field_keys?.length > 0
        ? queryConfigs.group_by_config?.field_keys[0]
        : "none";

    const datasetConfig = {
      groupByField: groupByFieldKey,
      additional_config: queryConfigs.additional_config,
      linkedEntity: queryConfigs.linked_entity_config
    };

    dispatch({
      type: "UPDATE_ALL_CONFIGS",
      payload: {
        detail: {
          id: customReportDetails.project_reports_by_pk.id,
          name: queryConfigs.name,
          description: queryConfigs.description
        },
        datasetConfig,
        columnConfig: queryConfigs.columns_config,
        gridState: {
          sortState: queryConfigs.sorts_config,
          filterModel: queryConfigs.filters_config
        },
        permissions: {
          canConfigureCustomReports
        }
      }
    });
  }, [dispatch, customReportDetails, canConfigureCustomReports]);

  const onUpdateProjectReport = useCallback(
    async (data: any, showSuccessMessage = false) => {
      if (!canConfigureCustomReports) return;

      const res = await updateCustomReport({
        variables: {
          project_report_id: customReportId,
          data
        }
      });
      if (res.data) {
        if (showSuccessMessage) {
          message.success(SuccessMessages.SuccessfullyUpdate);
        }
        // refetchCustomReport();
      } else {
        message.error(ErrorMessages.SomethingWentWrong);
      }
    },
    [canConfigureCustomReports, customReportId, updateCustomReport]
  );

  const onUpdateDatasetConfig = useCallback(
    async (datasetConfig: any) => {
      if (!canConfigureCustomReports) return;

      // console.log("onUpdateDatasetConfig: ", datasetConfig);
      await onUpdateProjectReport({
        linked_entity_config: datasetConfig.linkedEntity,
        group_by_config: {
          type: "simple",
          field_keys: [datasetConfig.groupByField]
        },
        additional_config: datasetConfig.additional_config
      });
    },
    [canConfigureCustomReports, onUpdateProjectReport]
  );

  const onUpdateColumnConfig = useCallback(
    async (columnConfig: any) => {
      if (!canConfigureCustomReports) return;

      // console.log("onUpdateColumnConfig: ", columnConfig);

      await onUpdateProjectReport({ columns_config: columnConfig });
    },
    [canConfigureCustomReports, onUpdateProjectReport]
  );

  return {
    onUpdateDatasetConfig,
    onUpdateColumnConfig,
    onUpdateProjectReport,
    canConfigureCustomReports
  };
}

export default useManageConfigs;
